import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = ["Order Issue", "Refund Request", "Technical Problem", "Other"];

export default function ContactPage() {
  useSEO({ title: "Contact Us", description: "Get in touch with the CardMyDog team. We respond within 24 hours.", path: "/contact" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !subject || !message) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { name, email, subject, message });
      setSent(true);
      toast.success("Message sent!");
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen py-16 md:py-24 flex items-center justify-center" data-testid="contact-sent">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: "rgba(46,204,113,0.1)" }}>
            <Send size={28} style={{ color: "var(--color-success)" }} />
          </div>
          <h2 className="font-heading text-3xl uppercase tracking-tight text-[var(--color-text-dark)] mb-3">Message sent</h2>
          <p className="font-body text-[var(--color-text-mid)]">We respond within 24 hours. Check your email for a reply.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 md:py-24" data-testid="contact-page">
      <div className="max-w-lg mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "rgba(245,166,35,0.1)" }}>
            <MessageSquare size={24} style={{ color: "var(--color-gold)" }} />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-[var(--color-text-dark)] mb-3">
            Contact Us
          </h1>
          <p className="font-body text-[var(--color-text-mid)]">
            We respond within 24 hours. Check our <a href="/faq" className="underline" style={{ color: "var(--color-gold)" }}>FAQ</a> for quick answers.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-6 space-y-5">
          <div>
            <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-[8px]" data-testid="contact-name-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-[8px]" data-testid="contact-email-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Subject</Label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-[var(--color-border)] rounded-[8px] px-4 py-2.5 bg-[var(--color-white)] text-[var(--color-text-dark)] font-body text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-gold)]"
              data-testid="contact-subject-select"
            >
              <option value="">Select a topic</option>
              {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Message</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How can we help?"
              rows={5}
              className="rounded-[8px] resize-none"
              data-testid="contact-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full font-heading tracking-widest uppercase py-3 rounded-[8px] transition-colors disabled:opacity-50"
            style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
            data-testid="contact-submit-btn"
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
